/**
 * Note Utility Functions
 * Helpers for manipulating musical notes
 */

/**
 * All notes in chromatic scale
 */
const NOTES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

/**
 * Parse a note string into its components
 * @param note - Note string (e.g., "C4", "D#5")
 * @returns Object with note name, octave, and MIDI number, or null if invalid
 */
export function parseNote(note: string): { name: string; octave: number; midi: number } | null {
  const match = note.match(/^([A-G]#?)(\d+)$/);
  if (!match || !match[1] || !match[2]) return null;

  const name = match[1];
  const octave = parseInt(match[2], 10);

  const noteIndex = NOTES.indexOf(name);
  if (noteIndex === -1) return null;

  // Calculate MIDI note number (C4 = 60)
  const midi = (octave + 1) * 12 + noteIndex;

  return { name, octave, midi };
}

/**
 * Convert MIDI note number to note string
 * @param midi - MIDI note number (0-127)
 * @returns Note string (e.g., "C4", "D#5")
 */
export function midiToNote(midi: number): string {
  // Clamp to valid MIDI range
  const clampedMidi = Math.max(0, Math.min(127, midi));
  
  const octave = Math.floor(clampedMidi / 12) - 1;
  const noteIndex = clampedMidi % 12;
  const noteName = NOTES[noteIndex];

  return `${noteName}${octave}`;
}

/**
 * Adjust a note by a number of semitones
 * @param note - Current note string (e.g., "C4")
 * @param semitones - Number of semitones to adjust (positive = up, negative = down)
 * @returns New note string, or original if invalid
 */
export function adjustNoteBySemitones(note: string, semitones: number): string {
  const parsed = parseNote(note);
  if (!parsed) return note;

  const newMidi = parsed.midi + semitones;
  return midiToNote(newMidi);
}

/**
 * Adjust a note by a number of octaves
 * @param note - Current note string (e.g., "C4")
 * @param octaves - Number of octaves to adjust (positive = up, negative = down)
 * @returns New note string, or original if invalid
 */
export function adjustNoteByOctaves(note: string, octaves: number): string {
  return adjustNoteBySemitones(note, octaves * 12);
}

/**
 * Move note up by one semitone
 * @param note - Current note string
 * @returns New note string
 */
export function moveNoteUp(note: string): string {
  return adjustNoteBySemitones(note, 1);
}

/**
 * Move note down by one semitone
 * @param note - Current note string
 * @returns New note string
 */
export function moveNoteDown(note: string): string {
  return adjustNoteBySemitones(note, -1);
}

/**
 * Move note up by one octave
 * @param note - Current note string
 * @returns New note string
 */
export function moveNoteUpOctave(note: string): string {
  return adjustNoteByOctaves(note, 1);
}

/**
 * Move note down by one octave
 * @param note - Current note string
 * @returns New note string
 */
export function moveNoteDownOctave(note: string): string {
  return adjustNoteByOctaves(note, -1);
}

/**
 * Validate if a string is a valid note
 * @param note - Note string to validate
 * @returns True if valid note format
 */
export function isValidNote(note: string): boolean {
  return parseNote(note) !== null;
}
